import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    SxProps,
    Theme,
    Box
} from '@mui/material';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CouponShare} from '../models/couponshares';
import {sumMoneyAmounts} from '../models/money';
import {PoolXNumberFormat} from '../common/CurrencyFormatter';
import {Button} from '../reusable-components';
import {CancelError, useCancel} from '../rounds/usePurchase';
import useCouponShare from '../couponShares/useCouponShare';
import SyndicateModal from './syndicatemodal';
import VoidConfirmDialog from './voidconfirmdialog';
import {getRoundName} from '../common/RoundHelper';
import {operatorAdviceCancelSuccess, operatorAdviceCancelInitiated, operatorAdviceCancelFailed} from "../App";

interface CouponsTableProps {
    coupons: CouponShare[];
    onCouponCancelled?: () => void;
}

const getTotalAmount = (coupon: CouponShare) =>
    sumMoneyAmounts(coupon.stake, coupon.fee, coupon.agentFee);

export default function CouponsTable({coupons, onCouponCancelled}: CouponsTableProps) {
    const {t} = useTranslation();
    const cancel = useCancel();
    const [syndicateModalOpen, setSyndicateModalOpen] = useState(false);
    const [voidDialogOpen, setVoidDialogOpen] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState<CouponShare | null>(null);
    const [couponToVoid, setCouponToVoid] = useState<CouponShare | null>(null);
    const couponShareData = useCouponShare(selectedCoupon ? parseInt(selectedCoupon.id) : 0);

    const handleCancel = async (coupon: CouponShare) => {
        try {
            operatorAdviceCancelInitiated({
                type: "cancel_initiated",
                couponShareId: coupon.id,
                roundId: coupon.coupon.roundId,
                amount: getTotalAmount(coupon),
                currency: coupon.stake?.currency,
            }, -1);
            const res = await cancel(coupon);
            if (res && res.transactionId) {
                const cancelSuccessEvent = {
                    type: "cancel_success",
                    transactionId: res.transactionId,
                    roundId: res.roundId,
                    amount: res.amount,
                    currency: res.currency,
                };
                operatorAdviceCancelSuccess(cancelSuccessEvent);
                if (onCouponCancelled) onCouponCancelled();
            }
        } catch (err) {
            console.error('Cancel failed in UI', err);
            const cancelError = err instanceof CancelError ? err : undefined;
            operatorAdviceCancelFailed({
                type: "cancel_failed",
                couponShareId: coupon.id,
                errorCode: cancelError?.errorCode ?? 100,
                errorMessage: cancelError?.localizedOperatorMessage ?? "Unable to cancel bet",
                operatorErrorCode: cancelError?.operatorErrorCode,
            }, -1);
            throw err;
        }
    }

    const handleVoidClick = (coupon: CouponShare) => {
        setCouponToVoid(coupon);
        setVoidDialogOpen(true);
    }

    const handleVoidConfirm = async () => {
        if (couponToVoid) {
            await handleCancel(couponToVoid);
        }
    }

    const handleOpenCouponDetails = (coupon: CouponShare) => {
        setSelectedCoupon(coupon);
        setSyndicateModalOpen(true);
    }

    const getStatusLabel = (couponShare: CouponShare) => {
        if (couponShare.status === 'CANCELED') {
            return t('coupon_share_status_cancelled');
        }
        if (couponShare.result) {
            return couponShare.result.amount > 0 ? 'Win' : 'Loss';
        }
        return 'Pending';
    }

    const tableHeadStyle: SxProps<Theme> = {
        backgroundColor: '#E2E2E2',
        '& th': {
            fontWeight: '500',
            fontSize: '15px',
            color: '#091F51',
            padding: '8px 12px',
            textAlign: 'left',
            borderBottom: '1px solid #CCCCCC'
        }
    };

    const tableBodyStyle: SxProps<Theme> = {
        '& tr': {
            '&:nth-of-type(odd)': {
                backgroundColor: '#F4F4F4'
            },
            '&:nth-of-type(even)': {
                backgroundColor: '#FAFAFA'
            },
            '&:hover': {
                backgroundColor: '#F0F0F0'
            }
        },
        '& td': {
            padding: '12px',
            fontSize: '20px',
            fontWeight: '500',
            color: '#091F51',
            borderBottom: '1px solid #E0E0E0'
        }
    };

    const containerStyle: SxProps<Theme> = {
        border: '1px solid #E0E0E0',
        borderRadius: '8px',
        overflow: 'hidden'
    };

    const intlOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    };
    const datef = new Intl.DateTimeFormat('sv-SV', intlOptions);

    return (
        <>
            <TableContainer component={Paper} sx={containerStyle}>
                <Table>
                    <TableHead sx={tableHeadStyle}>
                        <TableRow>
                            <TableCell>{t('round')}</TableCell>
                            <TableCell>{t('date')}</TableCell>
                            <TableCell>{t('paid')}</TableCell>
                            <TableCell>{t('stake')}</TableCell>
                            <TableCell>{t('id')}</TableCell>
                            <TableCell>{t('status')}</TableCell>
                            <TableCell align="right"/>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={tableBodyStyle}>
                        {coupons.map((coupon, index) => {
                            const createdAt = coupon.createdAt ? new Date(coupon.createdAt).getTime() : 0;
                            const expiry = createdAt + 10 * 60 * 1000;
                            const canCancel = expiry > Date.now();
                            const locale = t('locale');

                            const totAmount = Number(getTotalAmount(coupon));

                            const nf = PoolXNumberFormat(locale, {
                                style: 'currency',
                                currency: coupon.stake?.currency || 'SEK',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            });

                            const status = getStatusLabel(coupon);
                            const statusColor = status === 'Win' ? '#00AA00' : status === 'Loss' ? '#AA0000' : '#FFA500';

                            return (
                                <TableRow key={`coupon-${index}`}>
                                    <TableCell>
                                        {getRoundName(coupon.coupon.round, t) || '-'}
                                    </TableCell>
                                    <TableCell>
                                        {coupon.coupon.round.openTo ? datef.format(new Date(coupon.coupon.round.openTo)) : ''}
                                    </TableCell>
                                    <TableCell>
                                        {coupon.createdAt ? datef.format(new Date(coupon.createdAt)) : ''}
                                    </TableCell>
                                    <TableCell>
                                        {nf.format(totAmount)}
                                    </TableCell>
                                    <TableCell>
                                        {coupon.id || '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{color: statusColor, fontWeight: '600'}}>
                                            {status}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        {canCancel && (
                                            <Button
                                                style={{minWidth: '60px', fontSize: '20px'}}
                                                color="#FFFFFF"
                                                hoverColor="#FFFFFF"
                                                textColor="#4075FF"
                                                border="2px solid #4075FF"
                                                borderRadius="52px"
                                                onClick={() => handleVoidClick(coupon)}
                                            >
                                                Void
                                            </Button>)
                                        }

                                        <Button
                                            style={{minWidth: '60px', fontSize: '20px', marginLeft: '10px'}}
                                            color="#4075FF"
                                            hoverColor="#4075FF"
                                            borderRadius="52px"
                                            onClick={() => handleOpenCouponDetails(coupon)}
                                        >
                                            Öppna
                                        </Button>

                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {selectedCoupon && couponShareData && (
                <SyndicateModal
                    open={syndicateModalOpen}
                    onClose={() => {
                        setSyndicateModalOpen(false);
                        setSelectedCoupon(null);
                    }}
                    syndicateId={Number(couponShareData[1].data?.coupon.coupon.syndicateId)}
                    roundId={Number(couponShareData[1].data?.coupon?.roundId)}
                    purchase={{}}
                    readonly={true}
                />
            )}
            <VoidConfirmDialog
                open={voidDialogOpen}
                onClose={() => {
                    setVoidDialogOpen(false);
                    setCouponToVoid(null);
                }}
                onConfirm={handleVoidConfirm}
                couponId={couponToVoid?.id || ''}
            />
        </>
    );
}
